import React, { useEffect } from 'react'
import classNames from 'classnames'
import AttributedHtmlText from '#src/components/AttributedHtmlText'
import ContentfulQuery from '#src/containers/Contentful'
import InputHeader from '#src/components/Path/components/InputHeader'
import PageHeader from '#src/components/Path/components/PageHeader'
import QuestionHeader from '#src/components/Path/components/QuestionHeader'
import ReactMarkdown from 'react-markdown'
import ErrorHeader from '#src/components/Path/components/ErrorHeader'
import { extractGenericModalMetadata } from '#src/services/path'
import Interstitial, { InterstitialMap } from '#src/components/Path/Interstitials'
import MetaData, { MetaDataComponentMap } from '#src/components/Path/MetaData'
import {
  InterstitialPathFlowPageMetadataType,
  InputTextPathFlowQuestionFragment,
  NumericPathFlowQuestionFragment,
  ListPathFlowQuestionFragment
} from '#src/generated/graphql'
import Question, { QuestionComponentMap, PathQuestionProps } from '../../Question'
import { Button } from '@deal/components'
import { LayoutProps } from '../'
import { usePathContext } from '#src/components/Path/PathContext'
import styles from './styles.css'

interface QuizLayoutContentfulResponse {
  loading: boolean
  content: {
    title: string
  }
}

// Questions
import InputTextPathFlowQuestionComponent from '#src/components/Path/components/InputTextPathFlowQuestion'
import IdPathFlowQuestionComponent from '#src/components/Path/components/IdPathFlowQuestion'
import ListPathFlowQuestionComponent from '#src/components/Path/components/ListPathFlowQuestion'
import NumericPathFlowQuestionComponent from '#src/components/Path/components/NumericPathFlowQuestion'
import OAuthEmailPathFlowQuestionComponent from '#src/components/Path/components/OAuthEmailPathFlowQuestion'
import DateRangePathFlowQuestionComponent from '#src/components/Path/components/DateRangePathFlowQuestion'
import DatePathFlowQuestionComponent from '#src/components/Path/components/DatePathFlowQuestion'

// Interstitials
import GenericInterstitial from '#src/components/Path/components/GenericInterstitial'
import GenericWithButtonInterstitial from '#src/components/Path/components/GenericWithButtonInterstitial'
import RequestPushNotificationInterstitial from '#src/components/Path/components/RequestPushNotificationsInterstitial'
import WhatHappensNextInterstitial from '#src/components/Path/components/WhatHappensNextInterstitial'

// Metadata
import TermsPrivacyMedicalMetadata from '#src/components/Path/components/TermsPrivacyMedicalMetadata'
import TermsAndConditionsMetadata from '#src/components/Path/components/TermsAndConditionsMetadata'
import ContactPrivacyMetadata from '#src/components/Path/components/ContactPrivacyMetadata'
import ContactTCPAComplianceMetadata from '#src/components/Path/components/ContactTCPAComplianceMetadata'

const ListPathFlowQuestionComponentWrapper: React.SFC<PathQuestionProps<
  ListPathFlowQuestionFragment
>> = ({ ...rest }) => {
  return <ListPathFlowQuestionComponent {...rest} display="standard" />
}

const InputTextPathFlowQuestionWrapper: React.SFC<PathQuestionProps<
  InputTextPathFlowQuestionFragment
>> = ({ ...rest }) => {
  return <InputTextPathFlowQuestionComponent {...rest} display="standard" />
}

const NumericPathFlowQuestionWrapper: React.SFC<PathQuestionProps<
  NumericPathFlowQuestionFragment
>> = ({ ...rest }) => {
  return <NumericPathFlowQuestionComponent {...rest} display="standard" />
}

const WoodsLayout: React.SFC<LayoutProps> = ({
  areAllQuestionsOptional,
  areAllQuestionsAnswered,
  questions,
  className,
  onUpdateScrollRef
}) => {
  const questionMap: QuestionComponentMap = {
    IdPathFlowQuestion: IdPathFlowQuestionComponent,
    InputTextPathFlowQuestion: InputTextPathFlowQuestionWrapper,
    ListPathFlowQuestion: ListPathFlowQuestionComponentWrapper,
    NumericPathFlowQuestion: NumericPathFlowQuestionWrapper,
    OAuthEmailPathFlowQuestion: OAuthEmailPathFlowQuestionComponent,
    DateRangePathFlowQuestion: DateRangePathFlowQuestionComponent,
    DatePathFlowQuestion: DatePathFlowQuestionComponent
  }

  const interstitialMap: InterstitialMap = {
    [InterstitialPathFlowPageMetadataType.GENERIC_WITH_TITLE]: GenericInterstitial,
    [InterstitialPathFlowPageMetadataType.GENERIC_WITH_BUTTON]: GenericWithButtonInterstitial,
    [InterstitialPathFlowPageMetadataType.REQUEST_PUSH_NOTIFICATION_PERMISSION]: RequestPushNotificationInterstitial,
    [InterstitialPathFlowPageMetadataType.WHAT_HAPPENS_NEXT]: WhatHappensNextInterstitial
  }

  const metaDataMap: MetaDataComponentMap = {
    TermsPrivacyMedicalPathFlowPageMetadata: TermsPrivacyMedicalMetadata,
    TermsAndConditionsPathFlowPageMetadata: TermsAndConditionsMetadata,
    ContactPrivacyMetadata: ContactPrivacyMetadata,
    ContactTCPAComplianceMetadata: ContactTCPAComplianceMetadata
  }

  const pathContext = usePathContext()

  useEffect(() => {
    onUpdateScrollRef(window)
  }, [])

  const pathCategory = pathContext.pathFlow.category ? pathContext.pathFlow.category : undefined
  const areActionsHidden = pathContext.isSubmitButtonHidden && pathContext.isBackButtonHidden

  const submitButtonText = pathContext.pathFlowPage.submitButtonText
    ? pathContext.pathFlowPage.submitButtonText.text
    : 'Submit'
  const skipButtonText = pathContext.pathFlowPage.skipButtonText
    ? pathContext.pathFlowPage.skipButtonText.text
    : 'Skip'

  const genericModalMetadata = extractGenericModalMetadata(pathContext.pathFlowPage.metadata)

  return (
    <div className={classNames('render-path-default', styles.pageContainer, styles.fixedBody)}>
      <div className={classNames(styles.pageContainer, className)}>
        {pathCategory && pathCategory.slug && (
          <ContentfulQuery template="woodsPathLayout" variation={pathCategory.slug}>
            {({ content, loading }: QuizLayoutContentfulResponse) => {
              if (loading) {
                return null
              } else if (content) {
                return (
                  <div className={styles.quizHeader}>
                    <ReactMarkdown source={content.title} />
                  </div>
                )
              } else {
                return (
                  <div className={styles.quizHeader}>
                    Take a quick quiz and get <em>personalized recommendations</em>
                  </div>
                )
              }
            }}
          </ContentfulQuery>
        )}
        {!pathCategory ||
          (!pathCategory.slug && (
            <div className={styles.quizHeader}>
              Take a quick quiz and get <em>personalized recommendations</em>
            </div>
          ))}

        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${pathContext.pathFlowPage.completionPercentage}%` }}
          />
        </div>

        <div className={styles.container}>
          <ErrorHeader />

          <PageHeader className={styles.title} />

          {pathContext.pathFlowPage.notificationTexts.map(notification => (
            <h2 className={styles.subtitle} key={notification.text}>
              <AttributedHtmlText text={notification} />
            </h2>
          ))}

          {questions.length === 0 && (
            <div className={styles.interstitial}>
              <Interstitial interstitialMap={interstitialMap} submitButtonText={submitButtonText} />
            </div>
          )}

          {questions.length > 0 && (
            <form
              onSubmit={!pathContext.isSubmitting ? pathContext.onPageSubmitted : undefined}
              className={styles.form}
            >
              <div className={styles.formContainer}>
                <div className={styles.questionContainer}>
                  <div>
                    {questions.map((question, index) => {
                      return (
                        <React.Fragment key={index}>
                          <QuestionHeader
                            questionText={question.questionText}
                            questionSubText={question.questionSubText}
                            genericModalMetadata={genericModalMetadata}
                          />
                          <InputHeader
                            inputLabel={question.inputLabel}
                            inputIconImage={question.inputIconImage}
                          />
                          <Question
                            questionIndex={index}
                            question={question}
                            questionComponentMap={questionMap}
                          />
                        </React.Fragment>
                      )
                    })}
                  </div>
                  <div>
                    <MetaData metaDataComponents={metaDataMap} />
                  </div>
                </div>
              </div>

              {!areActionsHidden && (
                <div className={styles.actions}>
                  <div
                    className={classNames(styles.buttons, {
                      [styles.centerContent]: pathContext.isSubmitButtonHidden
                    })}
                  >
                    {!pathContext.isBackButtonHidden && (
                      <div>
                        <Button
                          className={styles.back}
                          type="button"
                          size="large"
                          variant="contrast-light"
                          tabIndex={2}
                          onClick={pathContext.onPageBack}
                          disabled={pathContext.isSubmitting}
                        >
                          {pathContext.isSubmitting && pathContext.isSubmitButtonHidden
                            ? 'Submitting...'
                            : 'Back'}
                        </Button>
                      </div>
                    )}
                    {!pathContext.isSubmitButtonHidden && (
                      <div className={styles.submit}>
                        <Button
                          type="button"
                          size="large"
                          tabIndex={1}
                          disabled={!pathContext.isFormValid || pathContext.isSubmitting}
                          onClick={() => pathContext.onPageSubmitted()}
                        >
                          {pathContext.isSubmitting
                            ? 'Submitting...'
                            : areAllQuestionsOptional
                            ? areAllQuestionsAnswered
                              ? submitButtonText
                              : skipButtonText
                            : submitButtonText}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default React.memo(WoodsLayout)
